import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import classNames from "classnames";
import { Button, Card, Col, Form, Input, Popover, Row, Space, Table, Tag } from "antd";
import Icon, { SearchOutlined } from "@ant-design/icons";
import { TableProps } from "antd/lib/table";
import { find, flatMap, isEmpty, map, trim, without } from "lodash";
import { DropdownInput, RangePicker } from "components";
import ResizableTitle from "components/ResizableTitle";
import useResizableTableHeader from "hooks/useResizableTableHeader";
import { getLocalTimeZone } from "utils/getLocalTimeZone";
import { getTotalDisplay } from "utils/getTotalDisplay";
import { saveFile } from "utils/saveFile";
import { DAY_FORMAT } from "constants/common";
import { AiccFeedbackFilterRequest, AiccFeedbackListRequest } from "types/dto/request/aiccCustomer";
import { AiccFeedbackAnalytics, AiccFeedbackRecord } from "types/model/aiccCustomer";
import * as aiccAPI from "services/aiccCustomer";
import { DropDown } from "assets/icons";
import useAiccLanguages from "../../useAiccLanguages";
import AdvancedSearch from "./AdvancedSearch";
import getColumns from "./columnConfig";
import {
  DOWNVOTE_TURN_OPTIONS,
  FEEDBACK_PAGE_LIMIT,
  FEEDBACK_TYPE_OPTIONS,
  FLUENCY_LEVEL_OPTIONS,
  MIN_DATE,
  POST_ACTION_OPTIONS,
} from "./constants";
import {
  selectEndDate,
  selectStartDate,
  UserFeedbackAdvancedSearch,
  useUserFeedbackStore,
} from "./useUserFeedbackStore";
import AbandonmentChart from "./charts/AbandonmentChart";
import DownvoteTurnChart from "./charts/DownvoteTurnChart";
import SatisfactionRateChart from "./charts/SatisfactionRateChart";
import TrendChart from "./charts/TrendChart";
import styles from "./UserFeedbackList.module.scss";

const EMPTY_ANALYTICS: AiccFeedbackAnalytics = { daily: [], downvoteTurns: [], abandonment: [] };

type FilterValue = string | number;

const FILTER_TAG_GROUPS: {
  field: keyof UserFeedbackAdvancedSearch;
  label: string;
  options: { label: string; value: FilterValue }[];
}[] = [
  { field: "types", label: "Feedback type", options: FEEDBACK_TYPE_OPTIONS },
  { field: "downvoteTurns", label: "Downvote at", options: DOWNVOTE_TURN_OPTIONS },
  { field: "postActions", label: "Post-Action", options: POST_ACTION_OPTIONS },
  { field: "fluencyLevels", label: "Fluency level", options: FLUENCY_LEVEL_OPTIONS },
];

function UserFeedbackList() {
  const history = useHistory();
  const [form] = Form.useForm<UserFeedbackAdvancedSearch>();
  const [isAdvancedSearchVisible, setIsAdvancedSearchVisible] = useState(false);

  const [analytics, setAnalytics] = useState<AiccFeedbackAnalytics>(EMPTY_ANALYTICS);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [records, setRecords] = useState<AiccFeedbackRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [emailKeyword, setEmailKeyword] = useState(useUserFeedbackStore.getState().customerEmail);

  const languages = useAiccLanguages();

  const startDate = useUserFeedbackStore(selectStartDate);
  const endDate = useUserFeedbackStore(selectEndDate);
  const languageIds = useUserFeedbackStore((state) => state.languageIds);
  const customerEmail = useUserFeedbackStore((state) => state.customerEmail);
  const advancedSearch = useUserFeedbackStore((state) => state.advancedSearch);
  const page = useUserFeedbackStore((state) => state.page);
  const limit = useUserFeedbackStore((state) => state.limit);
  const setDateRange = useUserFeedbackStore((state) => state.setDateRange);
  const setLanguageIds = useUserFeedbackStore((state) => state.setLanguageIds);
  const setCustomerEmail = useUserFeedbackStore((state) => state.setCustomerEmail);
  const setAdvancedSearch = useUserFeedbackStore((state) => state.setAdvancedSearch);
  const setPagination = useUserFeedbackStore((state) => state.setPagination);

  const scope = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      timezone: getLocalTimeZone(),
      language_ids: isEmpty(languageIds) ? undefined : languageIds,
    }),
    [startDate, endDate, languageIds]
  );

  useEffect(() => {
    setIsAnalyticsLoading(true);
    aiccAPI
      .fetchAiccFeedbackAnalytics(scope)
      .then(setAnalytics)
      .finally(() => setIsAnalyticsLoading(false));
  }, [scope]);

  const filterRequest = useMemo<AiccFeedbackFilterRequest>(
    () => ({
      ...scope,
      customer_email: customerEmail,
      types: isEmpty(advancedSearch.types) ? undefined : advancedSearch.types,
      downvote_turns: isEmpty(advancedSearch.downvoteTurns) ? undefined : advancedSearch.downvoteTurns,
      post_downvote_actions: isEmpty(advancedSearch.postActions) ? undefined : advancedSearch.postActions,
      user_fluency_levels: isEmpty(advancedSearch.fluencyLevels) ? undefined : advancedSearch.fluencyLevels,
    }),
    [scope, customerEmail, advancedSearch]
  );

  const tableRequest = useMemo<AiccFeedbackListRequest>(() => ({ ...filterRequest, page, limit }), [
    filterRequest,
    page,
    limit,
  ]);

  useEffect(() => {
    setIsTableLoading(true);
    aiccAPI
      .fetchAiccFeedbackList(tableRequest)
      .then((response) => {
        setRecords(response.items);
        setTotal(response.total);
      })
      .finally(() => setIsTableLoading(false));
  }, [tableRequest]);

  const languageOptions = useMemo(() => map(languages, ({ id, name }) => ({ label: name, value: id })), [languages]);

  const handleViewConversation = useCallback(
    ({ subUserId, language, conversationId }: AiccFeedbackRecord) => {
      history.push({
        pathname: `/aicc/customer/subusers/${subUserId}/languages/${language.id}/conversations/${conversationId}`,
        state: { scrollToTranscript: true },
      });
    },
    [history]
  );

  const columns = useMemo(() => getColumns(handleViewConversation), [handleViewConversation]);

  const columnsToDisplay = useResizableTableHeader<AiccFeedbackRecord>(columns, 100, 500);

  const handleRowClick: TableProps<AiccFeedbackRecord>["onRow"] = (record) => ({
    onClick: () => handleViewConversation(record),
  });

  const handleEmailSearch = () => {
    const keyword = trim(emailKeyword);
    setCustomerEmail(isEmpty(keyword) ? undefined : keyword);
  };

  const handleAdvancedSearch = (values: UserFeedbackAdvancedSearch) => {
    setAdvancedSearch(values);
    setIsAdvancedSearchVisible(false);
  };

  const handleRemoveFilter = (field: keyof UserFeedbackAdvancedSearch, value: FilterValue) => {
    setAdvancedSearch({ ...advancedSearch, [field]: without<FilterValue>(advancedSearch[field], value) });
  };

  const filterTags = flatMap(FILTER_TAG_GROUPS, ({ field, label, options }) =>
    map(advancedSearch[field] as FilterValue[], (value) => (
      <Tag key={`${field}-${value}`} closable onClose={() => handleRemoveFilter(field, value)}>
        {`${label}: ${find(options, { value })?.label ?? value}`}
      </Tag>
    ))
  );

  const handleExport = () => {
    setIsExporting(true);
    aiccAPI
      .exportAiccFeedbackList(filterRequest)
      .then((content) => saveFile(content, `user_feedback_${startDate}_${endDate}.xlsx`))
      .finally(() => setIsExporting(false));
  };

  return (
    <Card title="User Feedback" className="page-container" style={{ overflowX: "auto" }}>
      <Row className={styles.filters}>
        <Space size={12}>
          <RangePicker
            value={[moment(startDate, DAY_FORMAT), moment(endDate, DAY_FORMAT)]}
            minDate={moment(MIN_DATE, DAY_FORMAT)}
            required
            onChange={([start, end]) => start && end && setDateRange(start.format(DAY_FORMAT), end.format(DAY_FORMAT))}
          />
          <DropdownInput
            options={languageOptions}
            value={languageIds}
            onChange={setLanguageIds}
            placeholder="All languages"
          />
        </Space>
      </Row>

      <Row gutter={[16, 16]} className={styles.charts}>
        <Col span={24}>
          <SatisfactionRateChart data={analytics.daily} isLoading={isAnalyticsLoading} />
        </Col>
        <Col span={12}>
          <DownvoteTurnChart data={analytics.downvoteTurns} isLoading={isAnalyticsLoading} />
        </Col>
        <Col span={12}>
          <AbandonmentChart data={analytics.abandonment} isLoading={isAnalyticsLoading} />
        </Col>
        <Col span={24}>
          <TrendChart data={analytics.daily} isLoading={isAnalyticsLoading} />
        </Col>
      </Row>

      <Row
        justify="space-between"
        className={classNames(styles.tableFilters, { [styles.tableFiltersWithTags]: !isEmpty(filterTags) })}
      >
        <Space size={12}>
          <Input
            allowClear
            value={emailKeyword}
            className={styles.searchInput}
            placeholder="Search Customer Email"
            prefix={<SearchOutlined />}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setEmailKeyword(event.target.value)}
            onBlur={() => setEmailKeyword((previous) => previous?.trim())}
            onPressEnter={handleEmailSearch}
          />
          <Popover
            content={
              <AdvancedSearch
                form={form}
                initialValues={advancedSearch}
                visible={isAdvancedSearchVisible}
                onFinish={handleAdvancedSearch}
                onCancel={() => setIsAdvancedSearchVisible(false)}
              />
            }
            placement="bottomLeft"
            trigger={["click"]}
            visible={isAdvancedSearchVisible}
            onVisibleChange={setIsAdvancedSearchVisible}
            overlayInnerStyle={{ padding: "12px 8px 0 8px" }}
          >
            <Button type="default" shape="round">
              Advanced Search
              <Icon component={DropDown} />
            </Button>
          </Popover>
        </Space>

        <Button type="default" shape="round" loading={isExporting} onClick={handleExport}>
          Export Excel
        </Button>
      </Row>

      {!isEmpty(filterTags) && <div className={styles.filterTags}>{filterTags}</div>}

      <Table<AiccFeedbackRecord>
        scroll={{ x: "100%" }}
        loading={isTableLoading}
        dataSource={records}
        columns={columnsToDisplay}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        rowKey={(record) => record.id}
        onRow={handleRowClick}
        pagination={{
          showTotal: getTotalDisplay,
          current: page,
          pageSize: limit,
          total,
          defaultCurrent: 1,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => setPagination(nextPage, nextPageSize ?? FEEDBACK_PAGE_LIMIT),
        }}
      />
    </Card>
  );
}

export default UserFeedbackList;
